import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlined from "@mui/icons-material/StopCircleOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  overflowY: "auto",
  ...theme.scrollbarStyles
}));

const statusColor = {
  running: "primary",
  paused: "warning",
  done: "success",
  cancelled: "default"
};

const statusLabel = {
  running: "Enviando",
  paused: "Pausada",
  done: "Concluída",
  cancelled: "Cancelada"
};

// Status do DESTINATÁRIO. "Enviada" é só o aceite da Cloud API — a entrega real
// (ou a falha) chega depois, por webhook da Meta.
const recipientLabel = {
  pending: "Na fila",
  sent: "Enviada",
  delivered: "Entregue",
  read: "Lida",
  failed: "Não entregue",
  error: "Erro no envio",
  suppressed: "Cancelada (opt-out)"
};

const recipientColor = {
  pending: "default",
  sent: "info",
  delivered: "primary",
  read: "success",
  failed: "error",
  error: "error",
  suppressed: "warning"
};

// Tradução dos códigos da Meta que mais aparecem em disparo de marketing.
const metaErrorHint = {
  131026: "número não recebe (sem WhatsApp/inválido)",
  131049: "bloqueio de marketing da Meta (destinatário recebeu muita promoção)",
  130472: "número em experimento de limitação de marketing da Meta",
  131047: "fora da janela de 24h",
  132000: "quantidade de parâmetros diferente do template",
  131051: "tipo de mensagem não suportado",
  133010: "número não registrado na conta"
};

// CSV dos que não chegaram — serve para limpar a base antes do próximo disparo.
const downloadUndelivered = campaign => {
  // opt-out fica de fora: não é número ruim, é contato que pediu para parar
  const rows = (campaign?.recipients || []).filter(r =>
    ["failed", "error"].includes(r.status)
  );
  const linhas = [
    "numero;nome;status;codigo;motivo",
    ...rows.map(r =>
      [
        r.number,
        (r.name || "").replace(/;/g, ","),
        recipientLabel[r.status] || r.status,
        r.errorCode || "",
        (r.errorMessage || "").replace(/[;\r\n]/g, " ")
      ].join(";")
    )
  ];
  const blob = new Blob([`﻿${linhas.join("\n")}`], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nao-entregues-campanha-${campaign.id}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// Extrai o texto do BODY de um MetaTemplate (para preview do corpo).
const templateBody = template => {
  if (!template || !template.components) return "";
  let comps = template.components;
  if (typeof comps === "string") {
    try {
      comps = JSON.parse(comps);
    } catch {
      return "";
    }
  }
  const body = (comps || []).find(
    c => (c.type || "").toUpperCase() === "BODY"
  );
  return body?.text || "";
};

// Quantas variáveis {{1}}..{{n}} o corpo do template espera (maior índice).
const countTemplateVars = template => {
  const body = templateBody(template);
  const matches = body.match(/\{\{\s*(\d+)\s*\}\}/g) || [];
  return matches.reduce((max, m) => {
    const n = parseInt(m.replace(/\D/g, ""), 10);
    return n > max ? n : max;
  }, 0);
};

// Maior {{n}} num texto livre (mensagem do chip).
const countTextVars = text =>
  ((text || "").match(/\{\{\s*(\d+)\s*\}\}/g) || []).reduce((max, m) => {
    const n = parseInt(m.replace(/\D/g, ""), 10);
    return n > max ? n : max;
  }, 0);

// Substitui {{n}} pelos parâmetros; variável ausente vira marcador visível.
const renderTemplate = (body, params = []) =>
  (body || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
    const v = params[parseInt(n, 10) - 1];
    return v === undefined || v === "" ? `⟨var${n} vazia⟩` : v;
  });

// Prévia do chip: variáveis preenchidas e, nas variações {Olá|Oi}, a 1ª opção
// (no envio real cada contato recebe uma sorteada).
const renderChipPreview = (text, params = []) =>
  renderTemplate(text, params).replace(/\{([^{}]*\|[^{}]*)\}/g, (_, o) => o.split("|")[0]);

// Normaliza número BR: só dígitos; até 11 dígitos (DDD+número) → prefixa 55.
const normalizeNumber = raw => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length <= 11 ? `55${digits}` : digits;
};

// Detecta o separador dominante (TAB > ; > ,). Prioriza ; sobre , para o
// padrão pt-BR, em que a vírgula é decimal (R$ 150,00) e não separador.
const detectDelimiter = text => {
  const lines = (text || "")
    .split(/\r?\n/)
    .filter(l => l.trim())
    .slice(0, 5);
  const count = d =>
    lines.reduce((s, l) => s + (l.split(d).length - 1), 0);
  const tab = count("\t");
  const semi = count(";");
  const comma = count(",");
  if (tab > 0 && tab >= semi && tab >= comma) return "\t";
  if (semi > 0 && semi >= comma) return ";";
  if (comma > 0) return ",";
  return ";";
};

// Divide uma linha pelo separador informado, respeitando aspas ("a; b").
const splitLine = (line, delim = ";") => {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && ch === delim) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map(f => f.trim());
};

// Espelha o parser do backend: 1ª coluna = telefone, demais = variáveis.
// Ignora cabeçalho (1ª célula sem dígitos), linhas vazias e números duplicados.
const analyzeRecipients = text => {
  const seen = new Set();
  const recipients = [];
  let ignoredHeader = 0;
  let ignoredDup = 0;
  let ignoredEmpty = 0;
  const delim = detectDelimiter(text);

  (text || "").split(/\r?\n/).forEach(line => {
    if (!line.trim()) return;
    const fields = splitLine(line, delim);
    const number = normalizeNumber(fields[0]);
    if (!number) {
      ignoredHeader += 1; // provável cabeçalho ou linha sem telefone
      return;
    }
    if (seen.has(number)) {
      ignoredDup += 1;
      return;
    }
    seen.add(number);
    const params = fields.slice(1).map(f => f.trim());
    recipients.push({ number, params });
  });

  // Quantas variáveis o arquivo traz por linha (usa o máximo observado).
  const varsFound = recipients.reduce(
    (max, r) => (r.params.length > max ? r.params.length : max),
    0
  );

  return {
    recipients,
    varsFound,
    ignoredHeader,
    ignoredDup,
    ignoredEmpty
  };
};

// --- Detecção inteligente de colunas (sem IA), para importar arquivo cru ---

const PHONE_HEADER_RE = /(tel|fone|celular|\bcel\b|whats|contato|n[uú]mero|numero|phone|msisdn)/i;

// Uma célula "parece telefone" se, só com dígitos, tem 10 a 13 (BR: DDD+8/9 dígitos, com/sem DDI).
const isPhoneLike = cell => {
  const d = (cell || "").replace(/\D/g, "");
  return d.length >= 10 && d.length <= 13;
};

// Converte o texto cru (CSV/TXT) em matriz de células.
const parseRowsFromText = text => {
  const delim = detectDelimiter(text);
  return (text || "")
    .split(/\r?\n/)
    .filter(l => l.trim())
    .map(l => splitLine(l, delim));
};

// Núcleo da detecção: recebe a matriz de células (de CSV OU de Excel) e devolve:
//  - canonical: texto `telefone;var1;var2` (telefone sempre 1º), pronto p/ o backend
//  - info: o mapeamento detectado, para exibir e o usuário conferir
const detectFromRows = rowsIn => {
  const rows = (rowsIn || [])
    .map(r => (r || []).map(c => (c === null || c === undefined ? "" : String(c).trim())))
    .filter(r => r.some(c => c !== ""));

  if (!rows.length) return { canonical: "", info: null };

  const ncols = rows.reduce((m, r) => Math.max(m, r.length), 0);

  // Há cabeçalho se a 1ª linha não tem nenhuma célula telefônica mas os dados têm,
  // ou se a 1ª linha tem algum rótulo conhecido de telefone.
  const row0HasPhone = rows[0].some(isPhoneLike);
  const dataHasPhone = rows.slice(1).some(r => r.some(isPhoneLike));
  const hasHeader =
    !row0HasPhone && (dataHasPhone || rows[0].some(c => PHONE_HEADER_RE.test(c)));

  const header = hasHeader ? rows[0] : null;
  const body = hasHeader ? rows.slice(1) : rows;

  // 1) telefone pelo NOME da coluna no cabeçalho
  let phoneIdx = header ? header.findIndex(h => PHONE_HEADER_RE.test(h)) : -1;

  // 2) senão, pelo CONTEÚDO: a coluna com maior proporção de células telefônicas
  if (phoneIdx < 0) {
    let bestScore = 0;
    for (let c = 0; c < ncols; c += 1) {
      let hits = 0;
      let tot = 0;
      body.forEach(r => {
        if (r[c] !== undefined && r[c] !== "") {
          tot += 1;
          if (isPhoneLike(r[c])) hits += 1;
        }
      });
      const score = tot ? hits / tot : 0;
      if (score > bestScore) {
        bestScore = score;
        phoneIdx = c;
      }
    }
    if (bestScore < 0.5) phoneIdx = phoneIdx < 0 ? 0 : phoneIdx;
  }
  if (phoneIdx < 0) phoneIdx = 0;

  // Variáveis = todas as demais colunas, na ordem em que aparecem no arquivo.
  const varCols = [];
  for (let c = 0; c < ncols; c += 1) if (c !== phoneIdx) varCols.push(c);

  const canonical = body
    .map(r => [r[phoneIdx] || "", ...varCols.map(c => (r[c] || "").trim())].join(";"))
    .join("\n");

  const info = {
    hasHeader,
    phoneIdx,
    phoneHeader: header ? header[phoneIdx] : null,
    varHeaders: varCols.map(c => (header ? header[c] : null)),
    detectedByContent: !header || !PHONE_HEADER_RE.test(header[phoneIdx] || "")
  };

  return { canonical, info };
};

// CSV/TXT: texto → matriz → detecção.
const detectAndCanonicalize = text => detectFromRows(parseRowsFromText(text));

const Campaigns = () => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [connections, setConnections] = useState([]);
  // Modelos não oficiais (chip) — aba "Não oficiais" em Templates
  const [chipTemplates, setChipTemplates] = useState([]);
  const [chipTemplateId, setChipTemplateId] = useState("");
  const [templateMedia, setTemplateMedia] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    whatsappId: "",
    metaTemplateId: "",
    messageText: "",
    intervalSeconds: 5,
    intervalMaxSeconds: 90,
    dailyLimit: 150,
    recipientsText: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [importInfo, setImportInfo] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  // Lista vinda da tela de Contatos (seleção/filtro): resumo do que ficou de fora
  const [fromContacts, setFromContacts] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const mediaRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data.records || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/metaTemplates", {
        params: { status: "APPROVED" }
      });
      setTemplates(Array.isArray(data) ? data : data.records || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  // Contas que disparam: WhatsApp pela Evolution (oficial ou chip).
  const fetchConnections = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp");
      const list = Array.isArray(data) ? data : data.whatsapps || [];
      setConnections(list.filter(w => w.type === "waba" && w.session));
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchChipTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/chip-templates");
      setChipTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      // sem modelos não impede a tela
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCampaigns(), fetchTemplates(), fetchConnections(), fetchChipTemplates()]).finally(() =>
      setLoading(false)
    );
  }, [fetchCampaigns, fetchTemplates, fetchConnections, fetchChipTemplates]);

  // Veio de Contatos → abre a Nova campanha já com os destinatários.
  useEffect(() => {
    const vindo = location.state?.fromContacts;
    if (!vindo) return;
    setForm({
      name: "",
      whatsappId: "",
      metaTemplateId: "",
      messageText: "",
      intervalSeconds: 5,
      intervalMaxSeconds: 90,
      dailyLimit: 150,
      recipientsText: vindo.recipientsText || ""
    });
    setImportInfo(null);
    setMediaFile(null);
    setFromContacts(vindo);
    setChipTemplateId("");
    setTemplateMedia(null);
    setModalOpen(true);
    // limpa o state para um F5 não reabrir a janela
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Polling leve para acompanhar o progresso enquanto houver campanha enviando.
  useEffect(() => {
    const hasRunning = campaigns.some(c => c.status === "running");
    if (!hasRunning) return undefined;
    const id = setInterval(fetchCampaigns, 4000);
    return () => clearInterval(id);
  }, [campaigns, fetchCampaigns]);

  const handleOpenModal = () => {
    setForm({
      name: "",
      whatsappId: "",
      metaTemplateId: "",
      messageText: "",
      intervalSeconds: 5,
      intervalMaxSeconds: 90,
      dailyLimit: 150,
      recipientsText: ""
    });
    setImportInfo(null);
    setMediaFile(null);
    setFromContacts(null);
    setChipTemplateId("");
    setTemplateMedia(null);
    fetchChipTemplates();
    setModalOpen(true);
  };

  const applyImport = (canonical, info) => {
    if (!canonical) {
      toast.warning("Arquivo vazio ou sem linhas válidas.");
      return;
    }
    setForm(f => ({ ...f, recipientsText: canonical }));
    setImportInfo(info);
  };

  // Importa CSV/TXT ou Excel (.xlsx/.xls/.ods). Em ambos, detecta a coluna de
  // telefone e o cabeçalho automaticamente e normaliza para o formato canônico.
  // O parser de Excel (SheetJS) só é baixado quando um arquivo Excel é escolhido
  // (dynamic import → chunk separado, não pesa o carregamento do app).
  const handleFile = async e => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = ""; // permite reimportar o mesmo arquivo
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    const isExcel =
      name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".ods");
    try {
      if (isExcel) {
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: ""
        });
        const { canonical, info } = detectFromRows(rows);
        applyImport(canonical, info);
      } else {
        const text = await file.text();
        const { canonical, info } = detectAndCanonicalize(text);
        applyImport(canonical, info);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.warning("Informe o nome da campanha.");
      return;
    }
    if (!form.whatsappId) {
      toast.warning("Escolha a conta de envio.");
      return;
    }
    if (!isChip && !form.metaTemplateId) {
      toast.warning("Selecione um template aprovado.");
      return;
    }
    if (isChip && !form.messageText.trim() && !mediaFile && !templateMedia) {
      toast.warning("Escreva a mensagem ou anexe um arquivo.");
      return;
    }
    if (!form.recipientsText.trim()) {
      toast.warning("Cole ou importe a lista de destinatários.");
      return;
    }
    if (varsMismatch) {
      toast.warning(
        `A mensagem espera ${templateVars} variável(is), mas a lista traz ${analysis.varsFound}.`
      );
      return;
    }
    if (rowsMissingVars > 0) {
      toast.warning(
        `${rowsMissingVars} linha(s) têm menos variáveis que o template exige.`
      );
      return;
    }
    try {
      setSubmitting(true);
      const extrasChip = isChip
        ? {
            chipTemplateId: chipTemplateId || "",
            useTemplateMedia: !mediaFile && templateMedia ? "true" : "false"
          }
        : {};
      let payload = isChip ? { ...form, ...extrasChip } : { ...form, messageText: "" };
      if (isChip && mediaFile) {
        payload = new FormData();
        Object.entries({ ...form, ...extrasChip }).forEach(([k, v]) => payload.append(k, v));
        payload.append("media", mediaFile);
      }
      const { data } = await api.post("/campaigns", payload);
      toast.success("Campanha criada — o disparo já começou.");
      // Números que a lista de supressão tirou antes de disparar (não recebem
      // ou pediram para parar).
      if (data?.suppressedCount > 0) {
        toast.info(
          `${data.suppressedCount} número(s) foram removidos por estarem na ` +
            "lista de supressão (não recebem ou pediram para parar)."
        );
      }
      setModalOpen(false);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (campaign, status) => {
    try {
      await api.put(`/campaigns/${campaign.id}/status`, { status });
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async campaign => {
    try {
      await api.delete(`/campaigns/${campaign.id}`);
      toast.success("Campanha excluída.");
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
    setConfirm(null);
  };

  const openDetail = async campaign => {
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}`);
      setDetail(data);
    } catch (err) {
      toastError(err);
    }
  };

  const selectedConnection = connections.find(
    w => String(w.id) === String(form.whatsappId)
  );
  const isChip = !!selectedConnection && !selectedConnection.isOfficialWaba;
  const selectedTemplate = isChip
    ? null
    : templates.find(t => String(t.id) === String(form.metaTemplateId));

  // Análise da lista (vale tanto para texto colado quanto importado).
  const analysis = useMemo(
    () => analyzeRecipients(form.recipientsText),
    [form.recipientsText]
  );
  const templateVars = isChip
    ? countTextVars(form.messageText)
    : countTemplateVars(selectedTemplate);
  const body = isChip ? form.messageText : templateBody(selectedTemplate);
  // "Mensagem pronta" para validar: template escolhido ou texto do chip.
  const hasMessage = isChip ? !!(form.messageText.trim() || mediaFile || templateMedia) : !!selectedTemplate;

  // Incompatibilidade: nº de variáveis do arquivo ≠ nº esperado pelo template.
  const varsMismatch =
    hasMessage &&
    analysis.recipients.length > 0 &&
    analysis.varsFound !== templateVars;

  // Linhas com menos parâmetros que o template exige (ficariam com variável vazia).
  const rowsMissingVars = hasMessage
    ? analysis.recipients.filter(r => r.params.length < templateVars).length
    : 0;

  const canSubmit =
    !submitting &&
    form.name.trim() &&
    form.whatsappId &&
    hasMessage &&
    analysis.recipients.length > 0 &&
    !varsMismatch &&
    rowsMissingVars === 0;

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirm ? `Excluir a campanha "${confirm.name}"?` : "Excluir"}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => handleDelete(confirm)}
      >
        Os destinatários e o histórico desta campanha serão removidos. Esta ação
        não pode ser desfeita.
      </ConfirmationModal>

      {/* Modal de criação */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nova campanha (disparo em massa)</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome da campanha"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            select
            label="Enviar pela conta"
            fullWidth
            margin="dense"
            value={form.whatsappId}
            onChange={e => {
              // Chip exige no mínimo 30 s entre envios; oficial volta ao padrão 5 s.
              const w = connections.find(c => String(c.id) === String(e.target.value));
              const chip = !!w && !w.isOfficialWaba;
              setForm({
                ...form,
                whatsappId: e.target.value,
                intervalSeconds: chip ? Math.max(30, Number(form.intervalSeconds) || 30) : 5
              });
            }}
          >
            {connections.length === 0 && (
              <MenuItem value="" disabled>
                Nenhuma conta de WhatsApp disponível
              </MenuItem>
            )}
            {connections.map(w => (
              <MenuItem
                key={w.id}
                value={w.id}
                disabled={w.status !== "CONNECTED"}
              >
                {w.name}
                {w.number ? ` · ${w.number}` : ""}
                <Chip
                  size="small"
                  sx={{ ml: 1 }}
                  label={w.isOfficialWaba ? "API oficial" : "Não oficial"}
                  color={w.isOfficialWaba ? "success" : "warning"}
                  variant={w.isOfficialWaba ? "filled" : "outlined"}
                />
                {w.status !== "CONNECTED" && (
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                    desconectada
                  </Typography>
                )}
              </MenuItem>
            ))}
          </TextField>

          {isChip && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
              <b>Conta não oficial:</b> disparo em volume pode levar o WhatsApp a
              bloquear este número. Para reduzir o risco, o envio sai em intervalos
              aleatórios, respeita um limite diário por chip e só acontece das 8h às
              20h, de segunda a sábado. Prefira contatos que já conversam com você e
              inclua no texto como parar de receber (ex.: "Responda SAIR para não
              receber mais").
            </Alert>
          )}

          {isChip && (
            <>
              <TextField
                select
                label="Modelo (opcional)"
                fullWidth
                margin="dense"
                value={chipTemplateId}
                onChange={e => {
                  const id = e.target.value;
                  setChipTemplateId(id);
                  const m = chipTemplates.find(x => String(x.id) === String(id));
                  if (m) {
                    // preenche o texto (dá para ajustar só nesta campanha)
                    setForm(f => ({ ...f, messageText: m.body || "" }));
                    setTemplateMedia(m.mediaPath ? { name: m.mediaName || m.mediaPath } : null);
                    setMediaFile(null);
                  } else {
                    setTemplateMedia(null);
                  }
                }}
                helperText={
                  chipTemplates.length === 0
                    ? "Nenhum modelo cadastrado — crie em Templates › Não oficiais (chip), ou escreva abaixo."
                    : "Escolha um modelo cadastrado ou escreva o texto abaixo."
                }
              >
                <MenuItem value="">Texto livre (sem modelo)</MenuItem>
                {chipTemplates.map(m => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                    {m.mediaName ? " 📎" : ""}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Mensagem"
                fullWidth
                multiline
                minRows={4}
                margin="dense"
                value={form.messageText}
                onChange={e => setForm({ ...form, messageText: e.target.value })}
                helperText={
                  "Use {{1}}, {{2}}... para os dados da planilha e {Olá|Oi|Bom dia} " +
                  "para variar o texto entre os contatos (cada um recebe uma opção sorteada)."
                }
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <Button
                  size="small"
                  startIcon={<UploadFileOutlined />}
                  onClick={() => mediaRef.current && mediaRef.current.click()}
                >
                  {mediaFile ? "Trocar anexo" : "Anexar imagem ou PDF"}
                </Button>
                {mediaFile && (
                  <Chip
                    size="small"
                    label={mediaFile.name}
                    onDelete={() => setMediaFile(null)}
                  />
                )}
                {!mediaFile && templateMedia && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`Anexo do modelo: ${templateMedia.name}`}
                    onDelete={() => setTemplateMedia(null)}
                  />
                )}
                <input
                  ref={mediaRef}
                  type="file"
                  accept="image/*,video/mp4,application/pdf"
                  hidden
                  onChange={e => {
                    const f = e.target.files && e.target.files[0];
                    if (mediaRef.current) mediaRef.current.value = "";
                    if (f) setMediaFile(f);
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label="Intervalo mínimo (s)"
                  type="number"
                  margin="dense"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 30 }}
                  value={form.intervalSeconds}
                  onChange={e => setForm({ ...form, intervalSeconds: e.target.value })}
                />
                <TextField
                  label="Intervalo máximo (s)"
                  type="number"
                  margin="dense"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 30 }}
                  value={form.intervalMaxSeconds}
                  onChange={e => setForm({ ...form, intervalMaxSeconds: e.target.value })}
                />
                <TextField
                  label="Limite por dia no chip"
                  type="number"
                  margin="dense"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, max: 300 }}
                  value={form.dailyLimit}
                  onChange={e => setForm({ ...form, dailyLimit: e.target.value })}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Mínimo de 30 s entre envios; o limite diário vale para o chip,
                somando todas as campanhas dele.
              </Typography>
            </>
          )}

          {selectedConnection && !isChip && (
          <TextField
            select
            label="Template aprovado"
            fullWidth
            margin="dense"
            value={form.metaTemplateId}
            onChange={e =>
              setForm({ ...form, metaTemplateId: e.target.value })
            }
          >
            {templates.length === 0 && (
              <MenuItem value="" disabled>
                Nenhum template APROVADO — sincronize em Templates Oficiais
              </MenuItem>
            )}
            {templates.map(t => (
              <MenuItem key={t.id} value={t.id}>
                {t.name} ({t.language})
              </MenuItem>
            ))}
          </TextField>
          )}

          {selectedTemplate && (
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1,
                my: 1,
                fontSize: 13,
                whiteSpace: "pre-wrap"
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Modelo{" "}
                {templateVars > 0
                  ? `— ${templateVars} variável(is): ${Array.from(
                      { length: templateVars },
                      (_, i) => `{{${i + 1}}}`
                    ).join(" ")}`
                  : "— sem variáveis"}
              </Typography>
              <div>{body || "(sem corpo de texto)"}</div>
            </Box>
          )}

          {selectedConnection && !isChip && (
          <TextField
            label="Intervalo entre envios (segundos)"
            type="number"
            fullWidth
            margin="dense"
            inputProps={{ min: 1 }}
            value={form.intervalSeconds}
            onChange={e =>
              setForm({ ...form, intervalSeconds: e.target.value })
            }
          />
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5
            }}
          >
            <Typography variant="subtitle2">Destinatários</Typography>
            <Button
              size="small"
              startIcon={<UploadFileOutlined />}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              Importar (Excel/CSV)
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.ods,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,text/plain"
              hidden
              onChange={handleFile}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={5}
            margin="dense"
            placeholder={
              "Um por linha. Ex:\n5521988210010;João;Fatura Julho;10/07;150,00\n\nOu importe um Excel/CSV — a coluna de telefone é detectada automaticamente."
            }
            value={form.recipientsText}
            onChange={e => {
              setForm(f => ({ ...f, recipientsText: e.target.value }));
              setImportInfo(null);
            }}
            onPaste={e => {
              // Colagem robusta: insere o texto do clipboard na posição do
              // cursor manualmente, imune a qualquer interceptação do paste
              // padrão em componentes pais.
              const pasted = e.clipboardData && e.clipboardData.getData("text");
              if (pasted == null) return;
              e.preventDefault();
              const el = e.target;
              const cur = form.recipientsText || "";
              const start = el.selectionStart != null ? el.selectionStart : cur.length;
              const end = el.selectionEnd != null ? el.selectionEnd : cur.length;
              const next = cur.slice(0, start) + pasted + cur.slice(end);
              setForm(f => ({ ...f, recipientsText: next }));
              setImportInfo(null);
            }}
            helperText="1ª coluna = telefone; as demais = variáveis da mensagem ({{1}}, {{2}}...) na ordem. Separadores ; , ou TAB. Número BR sem DDI recebe 55 automaticamente. Você também pode importar Excel/CSV."
          />

          {fromContacts && (
            <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
              <b>{fromContacts.total} contato(s)</b> vindos da tela de Contatos.
              {(() => {
                const sk = fromContacts.skipped || {};
                const fora = [
                  sk.grupos ? `${sk.grupos} grupo(s)` : null,
                  sk.semNumero ? `${sk.semNumero} sem número de WhatsApp válido` : null,
                  sk.suprimidos ? `${sk.suprimidos} que pediram para sair ou não recebem` : null,
                  sk.duplicados ? `${sk.duplicados} repetido(s)` : null
                ].filter(Boolean);
                return fora.length ? ` Ficaram de fora: ${fora.join(", ")}.` : "";
              })()}
              {fromContacts.semNome > 0 &&
                ` ${fromContacts.semNome} sem nome salvo vão como "Cliente" no lugar do nome.`}
              {fromContacts.truncado && " ⚠️ O filtro tinha mais contatos que o limite de uma campanha; só os primeiros entraram."}
            </Alert>
          )}

          {/* Resultado da importação: mapeamento detectado */}
          {importInfo && (
            <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
              {importInfo.hasHeader
                ? "Cabeçalho detectado e ignorado. "
                : "Sem cabeçalho. "}
              Telefone ={" "}
              <b>
                {importInfo.phoneHeader
                  ? `coluna "${importInfo.phoneHeader}"`
                  : `coluna ${importInfo.phoneIdx + 1}`}
              </b>
              {importInfo.detectedByContent ? " (detectado pelo conteúdo)" : ""}.
              {importInfo.varHeaders.length > 0 && (
                <>
                  {" "}
                  Variáveis:{" "}
                  {importInfo.varHeaders
                    .map(
                      (h, i) => `${h ? `"${h}"` : `coluna`} → {{${i + 1}}}`
                    )
                    .join(", ")}
                  .
                </>
              )}
            </Alert>
          )}

          {/* Contagem e ignorados */}
          {analysis.recipients.length > 0 && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mt: 1 }}
            >
              {analysis.recipients.length} destinatário(s) válido(s)
              {analysis.ignoredDup > 0 &&
                ` · ${analysis.ignoredDup} duplicado(s) removido(s)`}
              {analysis.ignoredHeader > 0 &&
                ` · ${analysis.ignoredHeader} linha(s) sem telefone ignorada(s)`}
              .
            </Typography>
          )}

          {/* Validação de variáveis */}
          {varsMismatch && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
              {isChip ? "A mensagem" : <>O template <b>{selectedTemplate?.name}</b></>} espera{" "}
              <b>{templateVars}</b> variável(is), mas a lista traz{" "}
              <b>{analysis.varsFound}</b>. Ajuste as colunas —{" "}
              {analysis.varsFound > templateVars
                ? "há colunas de variável a mais."
                : "faltam colunas de variável."}
            </Alert>
          )}
          {!varsMismatch && rowsMissingVars > 0 && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
              {rowsMissingVars} linha(s) têm menos variáveis que o template
              exige — elas ficariam com campos vazios e seriam recusadas pela
              Meta.
            </Alert>
          )}
          {hasMessage &&
            !varsMismatch &&
            rowsMissingVars === 0 &&
            analysis.recipients.length > 0 && (
              <Alert severity="success" sx={{ mt: 1, fontSize: 12 }}>
                Variáveis conferem ({templateVars}) — pronto para disparar.
              </Alert>
            )}

          {/* Prévia de como a mensagem vai ficar (dados reais das 1ªs linhas) */}
          {hasMessage && body && analysis.recipients.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Prévia da mensagem (primeiros contatos):
              </Typography>
              {analysis.recipients.slice(0, 3).map((r, i) => (
                <Box
                  key={i}
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    p: 1,
                    mt: 0.5,
                    fontSize: 12,
                    whiteSpace: "pre-wrap"
                  }}
                >
                  <b>{r.number}</b>
                  {"\n"}
                  {isChip ? renderChipPreview(body, r.params) : renderTemplate(body, r.params)}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            Disparar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalhe */}
      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{detail?.name} — destinatários</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Nome/Param</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Detalhe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(detail?.recipients || []).map(r => {
                const hint = metaErrorHint[r.errorCode];
                const falhou = ["failed", "error"].includes(r.status);
                return (
                  <TableRow key={r.id}>
                    <TableCell>{r.number}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={recipientLabel[r.status] || r.status}
                        color={recipientColor[r.status] || "default"}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, wordBreak: "break-all" }}>
                      {falhou
                        ? `${r.errorCode ? `${r.errorCode} — ` : ""}${
                            hint || r.errorMessage || "Falha na entrega"
                          }`
                        : r.wamid}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => downloadUndelivered(detail)}
            disabled={
              !(detail?.recipients || []).some(r =>
                ["failed", "error"].includes(r.status)
              )
            }
          >
            Baixar não entregues (CSV)
          </Button>
          <Button onClick={() => setDetail(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>Campanhas (disparo em massa)</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Nova campanha">
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
              startIcon={<AddCircleOutline />}
            >
              Nova campanha
            </Button>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campanha</TableCell>
              <TableCell>Conta / mensagem</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Progresso</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map(c => {
              const done =
                c.sentCount + c.failedCount + (c.delivery?.suppressed || 0);
              const pct = c.total ? Math.round((done / c.total) * 100) : 0;
              return (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {c.channel === "unofficial"
                        ? `Texto livre${c.mediaName ? " + anexo" : ""}`
                        : c.templateName}
                    </Typography>
                    {c.whatsapp && (
                      <Typography variant="caption" color="textSecondary">
                        {c.whatsapp.name}
                        {c.channel === "unofficial" ? " (não oficial)" : " (API oficial)"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={statusLabel[c.status] || c.status}
                      color={statusColor[c.status] || "default"}
                    />
                    {/* Pausa automática de disjuntor: o operador precisa ver o
                        motivo, senão parece que a campanha travou sozinha. */}
                    {c.pausedReason && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="error"
                        sx={{ mt: 0.5, maxWidth: 220, mx: "auto" }}
                      >
                        ⚠️ {c.pausedReason}
                      </Typography>
                    )}
                    {c.status === "running" && c.waitReason && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                        sx={{ mt: 0.5, maxWidth: 220, mx: "auto" }}
                      >
                        ⏳ {c.waitReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{ mb: 0.5 }}
                    />
                    <Typography variant="caption" display="block">
                      {c.sentCount} enviadas de {c.total}
                      {c.failedCount > 0 && ` · ${c.failedCount} erro(s)`}
                    </Typography>
                    {/* Entrega confirmada pela Meta (chega depois do envio). */}
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {c.delivery?.delivered || 0} entregues ·{" "}
                      {c.delivery?.read || 0} lidas ·{" "}
                      <Box
                        component="span"
                        sx={{
                          color: c.delivery?.undelivered
                            ? "error.main"
                            : "inherit"
                        }}
                      >
                        {c.delivery?.undelivered || 0} não entregues
                      </Box>
                      {c.delivery?.suppressed > 0 &&
                        ` · ${c.delivery.suppressed} opt-out`}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver destinatários">
                      <IconButton size="small" onClick={() => openDetail(c)}>
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {c.status === "running" && (
                      <Tooltip title="Pausar">
                        <IconButton
                          size="small"
                          onClick={() => changeStatus(c, "paused")}
                        >
                          <PauseCircleOutline fontSize="small" color="warning" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {c.status === "paused" && (
                      <Tooltip title="Retomar">
                        <IconButton
                          size="small"
                          onClick={() => changeStatus(c, "running")}
                        >
                          <PlayCircleOutline fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {["running", "paused"].includes(c.status) && (
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => changeStatus(c, "cancelled")}
                        >
                          <StopCircleOutlined fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {["done", "cancelled", "paused"].includes(c.status) && (
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => setConfirm(c)}>
                          <DeleteOutline fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma campanha ainda.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

export default Campaigns;
